import styled from 'styled-components'
import { useDispatch, useSelector, } from 'react-redux'
import React, { useEffect, useMemo, useState, } from 'react'

import { RootState, getControlRoomRequestingOrderSummary, getUnassignedControlRoomRequestingOrders, getActiveControlRoomRequestingOrders, } from '@reducers'

import { CONTROL_ROOM_SUMMARY_POLLING_INTERVAL_MS, } from '@globals'

import ControlRoomRequestingList from '../ControlRoomRequestingList'

interface Props {
  className?: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 380px;
  height: calc(95vh - 120px);

  .summary-card {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    padding: 14px;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.96);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  }

  .summary-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 10px 8px;
    border-radius: 6px;
    background-color: rgb(245, 247, 250);
    min-height: 76px;
  }

  .summary-label {
    display: block;
    font-size: 12px;
    color: rgb(107, 119, 133);
    margin-bottom: 6px;
    text-align: center;
    white-space: nowrap;
  }

  .summary-value {
    font-size: 22px;
    font-weight: 700;
    color: rgb(48, 58, 71);
    width: 100%;
    text-align: center;
  }

  .summary-item.reminder {
    background-color: rgb(255, 239, 239);
  }

  .summary-item.reminder .summary-value {
    color: rgb(180, 36, 36);
  }

  .summary-placeholder {
    width: 100%;
    height: 26px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.8) 0%, rgba(241, 245, 249, 1) 50%, rgba(226, 232, 240, 0.8) 100%);
    background-size: 200% 100%;
    animation: summary-loading-shimmer 1.4s ease-in-out infinite;
  }

  @keyframes summary-loading-shimmer {
    0% {
      background-position: 200% 0;
    }

    100% {
      background-position: -200% 0;
    }
  }

  .tab-panel {
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.96);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
    overflow: hidden;
  }

  .tab-header {
    display: flex;
    gap: 6px;
    padding: 10px;
    background-color: rgb(52, 62, 76);
  }

  .tab-button {
    flex: 1;
    border: 0;
    border-radius: 7px;
    padding: 10px 12px;
    background-color: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.76);
    text-align: left;
    cursor: pointer;
    transition: 0.2s ease background-color, 0.2s ease color;
  }

  .tab-button.active {
    background-color: rgba(255, 255, 255, 0.95);
    color: rgb(44, 55, 68);
  }

  .tab-title {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  .tab-meta {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    opacity: 0.85;
  }

  .list-container {
    flex: 1;
    min-height: 0;
  }
`

type RequestingTabKey = 'unassigned' | 'active'

function ControlRoomRequestingSidebar(props: Props) {
  const { className, } = props

  const dispatch = useDispatch()
  const summary = useSelector((state: RootState) => state.controlRoomRequesting.summary)
  const [ activeTab, setActiveTab, ] = useState<RequestingTabKey>('unassigned')

  useEffect(
    () => {
      dispatch(getControlRoomRequestingOrderSummary())

      const interval = setInterval(
        () => dispatch(getControlRoomRequestingOrderSummary()),
        CONTROL_ROOM_SUMMARY_POLLING_INTERVAL_MS
      )

      return () => clearInterval(interval)
    },
    [ dispatch, ]
  )

  const currentTab = useMemo(
    () => {
      if(activeTab === 'unassigned') {
        return {
          key: 'unassigned',
          title: '미배차 오더',
          description: '현재 담당자 배정이 필요한 오더 목록입니다.',
          emptyDescription: '현재 미배차 상태인 오더가 없습니다.',
          count: summary?.unassigned_count,
          emphasisCount: summary?.needs_reminder_count,
          emphasisLabel: '리마인딩',
          reduxAction: getUnassignedControlRoomRequestingOrders,
          successState: 'SUCCESS_GET_UNASSIGNED_CONTROL_ROOM_REQUESTING_ORDERS',
        }
      }

      return {
        key: 'active',
        title: '배차 · 진행 오더',
        description: '배차 완료 또는 작업 진행중인 오더 목록입니다.',
        emptyDescription: '현재 배차되었거나 진행중인 오더가 없습니다.',
        count: (summary?.assigned_count || 0) + (summary?.in_progress_count || 0),
        emphasisCount: undefined,
        emphasisLabel: undefined,
        reduxAction: getActiveControlRoomRequestingOrders,
        successState: 'SUCCESS_GET_ACTIVE_CONTROL_ROOM_REQUESTING_ORDERS',
      }
    },
    [ activeTab, summary, ]
  )

  return (
    <Container className={ className }>
      <div className='summary-card'>
        <div className='summary-item'>
          <span className='summary-label'>미배차</span>
          <div className='summary-value'>
            { summary ? summary.unassigned_count : <div className='summary-placeholder' /> }
          </div>
        </div>
        <div className='summary-item'>
          <span className='summary-label'>배차완료</span>
          <div className='summary-value'>
            { summary ? summary.assigned_count : <div className='summary-placeholder' /> }
          </div>
        </div>
        <div className='summary-item'>
          <span className='summary-label'>진행중</span>
          <div className='summary-value'>
            { summary ? summary.in_progress_count : <div className='summary-placeholder' /> }
          </div>
        </div>
        <div className='summary-item reminder'>
          <span className='summary-label'>리마인딩 필요</span>
          <div className='summary-value'>
            { summary ? summary.needs_reminder_count : <div className='summary-placeholder' /> }
          </div>
        </div>
      </div>

      <div className='tab-panel'>
        <div className='tab-header'>
          <button
            className={ `tab-button ${ activeTab === 'unassigned' ? 'active' : '' }` }
            onClick={ () => setActiveTab('unassigned') }>
            <span className='tab-title'>미배차 오더</span>
            <span className='tab-meta'>
              { summary?.unassigned_count || 0 }건 / 리마인딩 { summary?.needs_reminder_count || 0 }건
            </span>
          </button>
          <button
            className={ `tab-button ${ activeTab === 'active' ? 'active' : '' }` }
            onClick={ () => setActiveTab('active') }>
            <span className='tab-title'>배차 · 진행 오더</span>
            <span className='tab-meta'>
              { (summary?.assigned_count || 0) + (summary?.in_progress_count || 0) }건
            </span>
          </button>
        </div>
        <div className='list-container'>
          <ControlRoomRequestingList
            key={ currentTab.key }
            title={ currentTab.title }
            description={ currentTab.description }
            emptyDescription={ currentTab.emptyDescription }
            count={ currentTab.count }
            countSuffix='건'
            emphasisCount={ currentTab.emphasisCount }
            emphasisLabel={ currentTab.emphasisLabel }
            reduxAction={ currentTab.reduxAction }
            successState={ currentTab.successState }
          />
        </div>
      </div>
    </Container>
  )
}

export default styled(ControlRoomRequestingSidebar)``
